import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_URL } from "../../utils/constants";
import { getDefaultHeaders } from "./getDefaultHeaders";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query/react";
import { getCookie } from "../../utils/token";
import { IResponse } from "../types";
import { logoutSuccess, updateToken } from "../actions/auth";
import { IAuthResponse } from "../types/auth.types";

let BASE_URL_DYNAMIC = BASE_URL;

const loadConfig = async () => {
  try {
    const response = await fetch("/config.json");
    const config = await response.json();
    BASE_URL_DYNAMIC = (window.location.hostname.includes("test") || window.location.hostname.includes("local")) ? config.serverUrlTest : config.serverUrl || BASE_URL;
  } catch (error) {
    console.error("Error loading config:", error);
  }
};

// This ensures that BASE_URL is loaded before you initialize your API
await loadConfig();

// Export a getter so other modules can reuse the runtime-resolved base URL
// without duplicating the `/config.json` loading logic.
export const getRuntimeBaseUrl = () => BASE_URL_DYNAMIC;

// Create our baseQuery instance 
const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL_DYNAMIC,
  // Send the bpreven.auth HttpOnly cookie on cross-origin API calls and
  // honor Set-Cookie from the login response. Required for FileProxy media
  // access via the cookie auth scheme. Safe: CORS allow-list is explicit
  // (no wildcard) and the cookie scheme is only accepted on the read-only
  // FileProxyController.
  credentials: "include",
  prepareHeaders: (headers, api) => {
    // Only set the default accessToken if Authorization header is not already set
    if (!headers.has("Authorization")) {
      const state = api.getState() as {
        user?: { accessToken?: string; activeContactToken?: string | null };
      };
      const token =
        getCookie("accessToken") ||
        getCookie("activeContactToken") ||
        state.user?.accessToken ||
        state.user?.activeContactToken ||
        undefined;
      return getDefaultHeaders(headers, api, token);
    }
    return getDefaultHeaders(headers, api, undefined);
  },
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // const refreshToken = (api.getState() as RootState).user.refreshToken;
  const refreshToken = getCookie("refreshToken");
  let result = await baseQuery(args, api, extraOptions);
  if (
    refreshToken &&
    result.error &&
    (result.error.status === 401 ||
      (result.error.status === 403 &&
        (result.error.data as IResponse).message === "jwt expired"))
  ) {
    const refreshResult = await baseQuery(
      {
        url: `/token`,
        method: "POST",
        body: {
          token: refreshToken,
        },
      },
      api,
      extraOptions
    );
    if (refreshResult.data) {
      api.dispatch(updateToken(refreshResult.data as IAuthResponse));
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logoutSuccess());
    }
  }
  return result;
};
